import { RepositoryFactory } from "@/repositories/RepositoryFactory";
import router from "@/router";

const ExercisesRepository = RepositoryFactory.get("exercises");

const state = {
  exercises: [],
  exercises_meta_last_update_ts: null,
  exercises_meta: {},
  exercise: {
    chapter: {},
    subchapter: {},
    levels: [],
  },
  currentQuestionId: null,
  autoResolve: false,
};

const getters = {
  getExercise: (state) => {
    return state.exercise;
  },
};

const actions = {
  create(context, exercise) {
    return ExercisesRepository.create(exercise)
      .then(() => {
        context.dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(`message.notifications.exercise.saveSuccess`),
          },
          { root: true },
        );
      })
      .catch(() => {
        context.dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.exercise.saveFailure`),
          },
          { root: true },
        );

        throw new Error();
      });
  },
  searchExercises({ commit, dispatch, state }, filters) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    return ExercisesRepository.searchExercises(filters)
      .then((response) => {
        const exercises = response.data;
        commit("SAVE_EXERCISES", exercises);
        commit("SAVE_EXERCISE", exercises[0]);
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(
              `message.notifications.exercise.getExercisesError`,
            ),
          },
          { root: true },
        );

        throw new Error();
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  searchQuestions({ commit, dispatch, state }, filters) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    return ExercisesRepository.searchQuestions(filters)
      .then((response) => {
        const exercise = response.data;
        commit("SAVE_EXERCISE", exercise);
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          dispatch(
            "notifications/addError",
            {
              message: state.$t(
                `message.notifications.exercise.getExerciseNotFound`,
              ),
            },
            { root: true },
          );
        } else {
          dispatch(
            "notifications/addError",
            {
              message: state.$t(
                `message.notifications.exercise.getExerciseError`,
              ),
            },
            { root: true },
          );
        }
        router.push({ name: "PersonalHome" });
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  loadExercise(context, id, locale) {
    const filters = {
      exercise_id: id,
      params: {
        search_questions_mode: context.rootGetters.isAdmin ? false : true,
        locale: locale,
      },
    };
    context.dispatch("searchQuestions", filters).finally(() => {
      context.dispatch("loadings/disableLoadings", {}, { root: true });
    });
  },
  loadExercisesMeta({ commit, dispatch, state }, locale) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    let payload = {
      locale: locale,
    };

    if (state.exercises_meta_last_update_ts !== null) {
      payload.ts = state.exercises_meta_last_update_ts;
    }

    return ExercisesRepository.getExercisesMeta(payload)
      .then((response) => {
        if (state.exercises_meta_last_update_ts != response.data.ts) {
          commit("SAVE_EXERCISES_META", response.data.content);
          commit("SAVE_EXERCISES_META_TS", response.data.ts);
        }
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(
              `message.notifications.exercise.getExercisesContentsError`,
            ),
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  resetTableOfContent({ dispatch, state }) {
    return ExercisesRepository.resetTableOfContent()
      .then(() => {
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(
              `message.notifications.tableofcontentResetSuccess`,
            ),
          },
          { root: true },
        );
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.tableofcontentResetError`),
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  setExercise({ commit }, exercise) {
    commit("SAVE_EXERCISE", exercise);
  },
  clearExercise({ commit }) {
    commit("CLEAR_EXERCISE");
  },
  saveCurrentQuestion({ commit }, id) {
    commit("SAVE_CURRENT_QUESTION", id);
  },
  clearCurrentQuestion({ commit }) {
    commit("CLEAR_CURRENT_QUESTION");
  },
};

const mutations = {
  SET_AUTORESOLVE(state, autoResolve) {
    state.autoResolve = autoResolve;
  },
  SAVE_EXERCISES(state, exercises) {
    state.exercises = exercises;
  },
  SAVE_EXERCISE(state, exercise) {
    state.exercise = exercise;
  },
  SAVE_EXERCISES_META(state, exercisesMeta) {
    state.exercises_meta = exercisesMeta;
  },
  SAVE_EXERCISES_META_TS(state, timestamp) {
    state.exercises_meta_last_update_ts = timestamp;
  },
  CLEAR_EXERCISE(state) {
    state.exercise = {
      chapter: {},
      subchapter: {},
      levels: [],
    };
  },
  SAVE_CURRENT_QUESTION(state, id) {
    state.currentQuestionId = id;
  },
  CLEAR_CURRENT_QUESTION(state) {
    state.currentQuestionId = null;
  },
};

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations,
};
